/** Which calls deserve a second look ("Calls to review"). Shared by the Dashboard tile and the call pop-up, so both use the same definition. */

/** How far back the card looks, how many calls it lists, and how short an enquiry can be before it counts as a hang-up. */
export const LOOK_BACK_DAYS = 7;
/** The pop-up lists at most this many calls (the tile counts all of them). */
export const MAX_LISTED = 50;
export const ENQUIRY_MIN_SECONDS = 30;

export interface CallToLookAt {
  id: string;
  caller_number: string | null;
  started_at: string | null;
  duration_seconds: number | null;
  outcome: string | null;
  needs_review: boolean;
  /** Set once someone has dismissed the call (migration 0053). */
  reviewed_at?: string | null;
}

export interface QuestionableItem {
  id: string;
  startedAt: string | null;
  caller: string | null;
  /** "review": a rule flagged something on the call. "enquiry": the caller talked for a while and nothing was booked, changed or left as a callback. */
  kind: "review" | "enquiry";
  reason: string;
}

/**
 * An enquiry that went on for a while (not a hang-up) and ended with no booking, no change and no callback request: how a caller the
 * receptionist could not help (a language it could not follow, a question it could not answer) looks. Not once it has been dismissed.
 */
export function isUnresolvedEnquiry(call: Pick<CallToLookAt, "outcome" | "duration_seconds" | "reviewed_at">, hasCallback: boolean): boolean {
  return (call.outcome ?? "").toLowerCase() === "enquiry" && (call.duration_seconds ?? 0) >= ENQUIRY_MIN_SECONDS && !hasCallback && !call.reviewed_at;
}

/**
 * The calls worth a second look, newest first:
 *   - a call with an open review flag (the voice service's own checks found something staff should confirm), and
 *   - an unresolved enquiry (see isUnresolvedEnquiry).
 * The reason shown is the flag's message, else the call summary's headline when the business has summaries, else a plain default.
 */
export function questionableCalls(
  calls: CallToLookAt[],
  headlines: ReadonlyMap<string, string>,
  flagMessages: ReadonlyMap<string, string>,
  callbackCallIds: ReadonlySet<string>
): { items: QuestionableItem[]; total: number } {
  const all: QuestionableItem[] = [];
  for (const c of calls) {
    if (c.needs_review) {
      all.push({
        id: c.id,
        startedAt: c.started_at,
        caller: c.caller_number,
        kind: "review",
        reason: flagMessages.get(c.id) ?? headlines.get(c.id) ?? "Something on this call needs a look.",
      });
    } else if (isUnresolvedEnquiry(c, callbackCallIds.has(c.id))) {
      all.push({
        id: c.id,
        startedAt: c.started_at,
        caller: c.caller_number,
        kind: "enquiry",
        reason: headlines.get(c.id) ?? "Ended without a booking or a callback request.",
      });
    }
  }
  all.sort((a, b) => (b.startedAt ?? "").localeCompare(a.startedAt ?? ""));
  return { items: all.slice(0, MAX_LISTED), total: all.length };
}
